import type { Category, Recurrence, Todo } from "../../domain/entities.ts";
import { currentTimezone } from "../../ui/date.ts";
import { dateInTimezone } from "./recurrence.ts";
import { normalizeReminders, REMINDER_OFFSETS } from "./reminders.ts";
import { composerTagOrder } from "./task-composer-placement.ts";
import { resolveWallTime } from "./task-time.ts";
import { applyCustomFieldForm } from "../custom-fields/model.ts";

export interface TaskFormMappingOptions {
  externalCalendars: Array<"google" | "microsoft">;
  previous?: Todo;
  randomId(): string;
  now?(): Date;
  category?(id: string | null): Category | undefined;
}

export function recurrenceFromForm(data: FormData, previousRule: Todo["recurrence"], timezone: string): Recurrence | null {
  const frequency = String(data.get("recurrenceFrequency") || "") as Recurrence["frequency"] | "";
  if (!frequency) return null;
  const monthlyMode = String(data.get("recurrenceMonthlyMode") || "day") as NonNullable<Recurrence["monthlyMode"]>;
  const endMode = String(data.get("recurrenceEndMode") || "never");
  return {
    frequency, interval: Math.max(1, Math.min(365, Number(data.get("recurrenceInterval")) || 1)), timezone,
    basis: data.get("recurrenceBasis") === "completion" ? "completion" : "scheduled",
    ...(frequency === "weekly" && data.getAll("recurrenceWeekdays").length ? { weekdays: data.getAll("recurrenceWeekdays").map(Number).filter(day => day >= 0 && day <= 6) } : {}),
    ...(frequency === "monthly" ? { monthlyMode, ordinal: monthlyMode === "ordinal" ? Number(data.get("recurrenceOrdinal")) || 1 : null, weekday: monthlyMode === "ordinal" ? Number(data.get("recurrenceWeekday")) : null } : {}),
    endDate: endMode === "date" ? String(data.get("recurrenceEndDate") || "") || null : null,
    occurrenceCount: endMode === "count" ? Math.max(1, Math.min(100000, Number(data.get("recurrenceCount")) || 1)) : null,
    skippedDates: [...(previousRule?.skippedDates || [])],
  };
}

export function applyTaskForm(todo: Todo, form: HTMLFormElement, data: FormData, options: TaskFormMappingOptions): void {
  todo.title = String(data.get("title") || "").trim();
  todo.description = String(data.get("description") || "").trim();
  todo.type = data.get("type") as Todo["type"];
  todo.subtasks = todo.type === "checklist" ? Array.from(form.querySelectorAll<HTMLElement>("[data-subtask]")).map(row => ({
    id: row.dataset.subtask!,
    title: (row.querySelector<HTMLInputElement>("[name=subtaskTitle]")?.value || "").trim(),
    note: (row.querySelector<HTMLTextAreaElement>("[name=subtaskNote]")?.value || "").trim(),
    completed: row.querySelector<HTMLInputElement>("[name=subtaskCompleted]")?.checked || false,
  })).filter(item => item.title) : [];
  todo.dueDate = String(data.get("dueDate") || "") || null;
  todo.priority = data.get("priority") as Todo["priority"];
  todo.completed = data.has("completed");
  todo.startDate = String(data.get("startDate") || "") || null;
  if (form.elements.namedItem("checkpoint")) todo.checkpoint = data.has("checkpoint");
  const startAt = String(data.get("startAt") || "");
  const endAt = String(data.get("endAt") || "");
  todo.scheduleMode = data.get("scheduleMode") === "floating" ? "floating" : "fixed";
  todo.scheduleTimeZone = todo.scheduleMode === "floating" ? String(data.get("scheduleTimeZone") || currentTimezone()).trim() : null;
  todo.wallStartAt = todo.scheduleMode === "floating" ? startAt || null : null;
  todo.wallEndAt = todo.scheduleMode === "floating" ? endAt || null : null;
  todo.startAt = startAt ? todo.scheduleMode === "floating" ? resolveWallTime(startAt, todo.scheduleTimeZone!) : new Date(startAt).toISOString() : null;
  todo.endAt = endAt ? todo.scheduleMode === "floating" ? resolveWallTime(endAt, todo.scheduleTimeZone!) : new Date(endAt).toISOString() : null;
  todo.estimatedMinutes = Math.max(0, Number(data.get("estimatedMinutes")) || 0) || null;
  if (todo.completed && !options.previous?.completed) todo.completedAt = (options.now?.() || new Date()).getTime();
  else if (!todo.completed) todo.completedAt = null;

  const exactReminderTimes = [...new Set(data.getAll("exactReminderAt").map(String).filter(Boolean).map(value => new Date(value).toISOString()))];
  const recipients = data.getAll("reminderRecipients").map(String);
  const previousReminders = new Map(normalizeReminders(todo).map(item => [`${item.mode}:${item.mode === "absolute" ? item.at : item.offsetMinutes}`, item.id]));
  todo.reminders = [
    ...data.getAll("reminderOffsets").map(Number).filter(offset => REMINDER_OFFSETS.includes(offset as typeof REMINDER_OFFSETS[number])).map(offset => ({ id: previousReminders.get(`relative:${offset}`) || options.randomId(), mode: "relative" as const, at: null, offsetMinutes: offset, recipients: [...recipients] })),
    ...exactReminderTimes.map(at => ({ id: previousReminders.get(`absolute:${at}`) || options.randomId(), mode: "absolute" as const, at, offsetMinutes: null, recipients: [...recipients] })),
  ];
  todo.reminderAt = todo.reminders.find(item => item.mode === "absolute")?.at || null;

  const previousRule = todo.recurrence;
  todo.recurrence = recurrenceFromForm(data, previousRule, todo.scheduleMode === "floating" ? todo.scheduleTimeZone! : previousRule?.timezone || currentTimezone());
  if (todo.recurrence && !todo.recurrenceSeriesId) {
    todo.recurrenceSeriesId = todo.id;
    todo.recurrenceOccurrence = 0;
    todo.recurrenceAnchorDate = todo.dueDate || todo.startDate || todo.wallStartAt?.slice(0, 10) || (todo.startAt ? dateInTimezone(new Date(todo.startAt), todo.recurrence.timezone) : dateInTimezone(options.now?.() || new Date(), todo.recurrence.timezone));
  }

  todo.categoryId = String(data.get("categoryId") || "") || null;
  if (data.has("epicId")) todo.epicId = String(data.get("epicId") || "") || null;
  todo.cycleId = String(data.get("cycleId") || "") || null;
  todo.externalCalendars = options.externalCalendars;
  todo.assignedTo = data.getAll("assignedTo").map(String);
  todo.dependencyIds = data.getAll("dependencyIds").map(String);
  todo.parentTaskId = String(data.get("parentTaskId") || "") || null;
  if (!todo.parentTaskId && options.previous?.parentTaskId && !data.has("epicId")) todo.epicId = options.previous.epicId || null;
  todo.relationships = [
    ...data.getAll("relatedTaskIds").map(taskId => ({ taskId: String(taskId), type: "related" as const })),
    ...(String(data.get("duplicateTaskId") || "") ? [{ taskId: String(data.get("duplicateTaskId")), type: "duplicate" as const }] : []),
  ];
  const category = options.category?.(todo.categoryId);
  const tags = composerTagOrder(String(data.get("tags") || "").split(","), category, String(data.get("boardColumn") || "") || null);
  todo.tags = Object.fromEntries(tags.map(tag => [tag, true]));
  todo.tagOrder = tags;
  applyCustomFieldForm(todo, form);
}
